import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2, Star , Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientFeedbackForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: "",
    client_name: "",
    client_contact: "",
    date: new Date().toISOString().split("T")[0],
    project_manager: "",
    feedback_categories: [
      { category: "Communication", rating: 0, comments: "" },
      { category: "Quality of Deliverables", rating: 0, comments: "" },
      { category: "Timeline Adherence", rating: 0, comments: "" },
      { category: "Budget Management", rating: 0, comments: "" },
      { category: "Team Professionalism", rating: 0, comments: "" }
    ],
    overall_rating: 0,
    would_recommend: true,
    additional_comments: "",
    testimonial: ""
  });

  const getSampleData = () => ({
    project_name: "Enterprise CRM Implementation",
    client_name: "ACME Corporation",
    client_contact: "Sarah Johnson, IT Director",
    date: new Date().toISOString().split("T")[0],
    project_manager: "John Smith",
    feedback_categories: [
      { category: "Communication", rating: 5, comments: "Excellent communication throughout the project. Regular updates and quick responses." },
      { category: "Quality of Deliverables", rating: 5, comments: "All deliverables met or exceeded expectations. Very thorough documentation." },
      { category: "Timeline Adherence", rating: 4, comments: "Project was delivered on time with minor delays in Phase 2 that were well communicated." },
      { category: "Budget Management", rating: 5, comments: "Came in under budget. All costs were transparent and well justified." },
      { category: "Team Professionalism", rating: 5, comments: "The team was professional, knowledgeable, and a pleasure to work with." }
    ],
    overall_rating: 5,
    would_recommend: true,
    additional_comments: "Overall, we are very satisfied with the project outcome. The team went above and beyond to ensure our requirements were met. The training provided to our staff was comprehensive and effective.",
    testimonial: "Working with this team transformed our customer management processes. The CRM solution they delivered has improved our sales efficiency by 40% and our customer satisfaction scores have never been higher. Highly recommended!"
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Client Feedback Form Draft";
      const payload = {
        name: draftName,
        template_type: "Client Feedback Form",
        template_id: "client-feedback",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "client-feedback", "Client Feedback", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("client-feedback", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/client-feedback`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Client_Feedback_${formData.project_name || "Document"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Client Feedback Form",
        formData,
        settings: activeSettings || {},
        templateType: "Client Feedback Form",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateCategory = (index, field, value) => {
    const updated = [...formData.feedback_categories];
    updated[index][field] = field === "rating" ? parseInt(value) || 0 : value;
    setFormData({ ...formData, feedback_categories: updated });
  };

  const addCategory = () => {
    setFormData({
      ...formData,
      feedback_categories: [...formData.feedback_categories, { category: "", rating: 0, comments: "" }]
    });
  };

  const removeCategory = (index) => {
    setFormData({ ...formData, feedback_categories: formData.feedback_categories.filter((_, i) => i !== index) });
  };

  const renderStars = (rating, onChange) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className={`p-1 ${star <= rating ? "text-yellow-400" : "text-gray-300"}`}
        >
          <Star className="h-6 w-6 fill-current" />
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Client Feedback Form</h1>
                <p className="text-sm text-gray-500">Collect and document client satisfaction feedback</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Feedback Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Client Name</Label>
                <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Client organization" />
              </div>
              <div>
                <Label>Client Contact</Label>
                <Input value={formData.client_contact} onChange={(e) => setFormData({ ...formData, client_contact: e.target.value })} placeholder="Name, Role" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <Label>Project Manager</Label>
                <Input value={formData.project_manager} onChange={(e) => setFormData({ ...formData, project_manager: e.target.value })} placeholder="PM name" />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Feedback Categories</Label>
              {formData.feedback_categories.map((cat, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <div>
                      <Label>Category</Label>
                      <Input value={cat.category} onChange={(e) => updateCategory(index, "category", e.target.value)} placeholder="Category name" />
                    </div>
                    <div>
                      <Label>Rating</Label>
                      {renderStars(cat.rating, (rating) => updateCategory(index, "rating", rating))}
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" size="icon" onClick={() => removeCategory(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label>Comments</Label>
                    <Input value={cat.comments} onChange={(e) => updateCategory(index, "comments", e.target.value)} placeholder="Feedback comments" />
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addCategory}>
                <Plus className="h-4 w-4 mr-2" /> Add Category
              </Button>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="text-lg font-semibold">Overall Rating</Label>
                <div className="mt-2">
                  {renderStars(formData.overall_rating, (rating) => setFormData({ ...formData, overall_rating: rating }))}
                </div>
              </div>
              <div>
                <Label className="text-lg font-semibold">Would Recommend?</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Switch 
                    checked={formData.would_recommend} 
                    onCheckedChange={(checked) => setFormData({ ...formData, would_recommend: checked })} 
                  />
                  <span className={formData.would_recommend ? "text-green-600" : "text-gray-500"}>
                    {formData.would_recommend ? "Yes, would recommend" : "No"}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <Label>Additional Comments</Label>
              <Textarea 
                value={formData.additional_comments} 
                onChange={(e) => setFormData({ ...formData, additional_comments: e.target.value })} 
                placeholder="Any additional feedback or suggestions..." 
                rows={4} 
              />
            </div>

            <div>
              <Label>Testimonial (Optional)</Label>
              <Textarea 
                value={formData.testimonial} 
                onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })} 
                placeholder="Would you be willing to provide a testimonial we can use?" 
                rows={3} 
              />
              <p className="text-xs text-gray-500 mt-1">This testimonial may be used for marketing purposes with your approval.</p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ClientFeedbackForm;
